import { getJSON, NOT_MODIFIED } from './apiClient';

// ─────────────────────────────────────────────────────────────────────────────
// Delivery drivers, for the expo screen's serve dialog.
//
// The SAME endpoint the till's Delivery modal uses — `/api/v1/drivers`, cashier
// auth, `Security_Users` with RoleID 6. The kitchen tablet already holds a
// cashier JWT (every KDS route is behind `cashierAuth`), so there is nothing to
// add on the server and, more importantly, nothing to keep in step: one list of
// drivers, defined once.
//
// Cached in module scope for five minutes, matching the cashier's react-query
// staleTime. Drivers do not change during a service, and the dialog opens on
// the hot path — a cook holding a bag should not wait on a round trip that
// answered the same thing ninety seconds ago.
// ─────────────────────────────────────────────────────────────────────────────

export interface Driver {
  id: number;
  name: string | null;
  name_ar: string | null;
  phone: string | null;
}

const TTL_MS = 5 * 60 * 1000;

let cache: { at: number; drivers: Driver[] } | null = null;
/** In-flight request, so two cards opening at once make one call. */
let inFlight: Promise<Driver[]> | null = null;

export function cachedDrivers(): Driver[] | null {
  if (!cache || Date.now() - cache.at > TTL_MS) return null;
  return cache.drivers;
}

export async function fetchDrivers(): Promise<Driver[]> {
  const fresh = cachedDrivers();
  if (fresh) return fresh;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const result = await getJSON<{ data: Driver[] }>('/api/v1/drivers');
      // A 304 means the server confirmed what we already had. That can only
      // happen when a previous call filled the cache, so falling back to it is
      // correct — and returning [] here would empty the picker on a refresh.
      if (result === NOT_MODIFIED) return cache?.drivers ?? [];
      const drivers = result.data ?? [];
      cache = { at: Date.now(), drivers };
      return drivers;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}
